import {LoggingActions} from '@/constants/loggingActions';
import {
    closeVideoCallWidget,
    openVideoCallWidget,
    showCallNotification,
    updateVideoCallsList,
    updateVideoNotifications,
} from '@/redux/commonCalls/slice';
import {
    ActiveCall,
    ActivePatientResponseDTO,
    GetVideoCallsResponseDTO,
    VideoCallResponseDTO,
    VideoProvider,
} from '@/redux/commonCalls/types';
import {store} from '@/redux/store';
import audioServiceFactory from '@/services/createAudioNodes';
import {loggerService} from '@/services/loggerService';
import {VideoMeetingStatusDTO} from '@/types/gatewayDataModels';
import {vidyoProviderLogs} from '@/utils/general';

import awsConnectService from './awsConnectApiService';
import httpApi from './httpApi_new';

type LogEventDataType = {
    severity: string;
    email: string;
    action: string;
    message: string;
};

type CallData = ActiveCall;

type VideoCallStatus = {
    videoProvider: VideoProvider;
    videoMeetingStatusDTO: VideoMeetingStatusDTO;
};

type CommonUserDataReducer = {
    phoneCallData: {
        awsConnectAgentStatus: string;
        isDoNotDisturbedEnabled: boolean;
    };
    user: {
        email: string;
    };
};

type VideoStore = {
    commonUserDataReducer: CommonUserDataReducer;
    callReducer: {
        activeVideoCallsList: CallData[];
    };
};

type AgentState = {
    name: string;
};

const VIDEO_CALL_USER_KEY = 'USER';
const MEETING_ID_KEY = `MEETING_ID`;
const VIDEO_CALL_STATUS_URL_ID_KEY = `VIDEO_CALL_STATUS_URL`;
const VIDEO_CALL_DATA_KEY = `VIDEO_CALL_DATA`;
const ON_VIDEO_CALL = `On a Video Call`;
const OFFLINE = `Offline`;
const TRUE = 'true';
const PROVIDER_AGNOSTIC_BASE_URL = '/command-center/v1/mission-control/video/meeting';

const mapVideoCallResponseToActiveCall = (videoCallResponse: VideoCallResponseDTO[]): ActiveCall[] =>
    videoCallResponse.map((videoCall) => ({
        ...videoCall.videoMeeting,
        meetingId: String(videoCall.videoMeeting?.meetingId),
        videoProvider: videoCall.videoProvider,
    }));

const getMeetingIdFromCallData = (callData: CallData): string => {
    return callData?.meetingId ?? (callData?.joinUrl as string)?.split('/')?.slice(-1)?.[0];
};

const getZoomConnectUrlFromCallData = (callData: CallData): string =>
    (callData?.joinUrl as string) ?? callData?.startUrl ?? '';

const VIDEO_PROVIDER_SETTINGS = {
    ZOOM: {
        providerSpecificBaseUrl: '/command-center/v1/mission-control/video/meeting',
        isWidget: false,
        portalUrl: '',
    },
    VIDYO: {
        providerSpecificBaseUrl: '/command-center/v1/mission-control/video/meeting',
        isWidget: true,
        portalUrl: `${window.VIDEO_SCHEDULED_ACCESS}`,
    },
};

const URLS = {
    /// Shared
    VIDEO: (url: string) => `${url}`,
    VIDEO_ACTIVE: ({url, patientId}: {patientId: number; url: string}) => `${url}/${patientId}/active`,
    PATIENT: ({url, patientId}: {patientId: number; url: string}) => `${url}/${patientId}`,
    /// Vendor specific
    SHARED_LINK_URL: ({meetingId, portalUrl}: {meetingId: string; portalUrl: string}) =>
        // `${portalUrl}/join/${meetingId}`, <--- fallback in case product decide to also use Vidyo portal
        `${portalUrl}?roomId=${meetingId}&isLive=true&videoProvider=VIDYO`,
    OPEN_NEW_WINDOW_LINK_URL: ({meetingId, portalUrl}: {meetingId: string; portalUrl: string}) =>
        `https://static.platform.vidyo.io/app/vidyoconnect/latest/webrtc/conf.htm?portal=${portalUrl}&roomKey=${meetingId}`,
    ACCEPT_CALL: ({url, meetingId}: {url: string; meetingId: string}) => `${url}/${meetingId}/accept`,
    DECLINE_CALL: ({url, meetingId}: {url: string; meetingId: string}) => `${url}/${meetingId}/decline`,
    LEAVE_CALL: ({url, meetingId}: {url: string; meetingId: string}) => `${url}/${meetingId}/leave`,
    STATUS: ({url, meetingId}: {url: string; meetingId: string}) => `${url}/${meetingId}/status`,
    INVITE_TRANSLATOR: ({meetingId, sipNumber, url}: {meetingId: string; sipNumber: string; url: string}) =>
        `${url}/${meetingId}/invitations/${sipNumber}`,
    VIDEO_SEND_TO_CALLBACK: (meetingId: string) =>
        `/command-center/v1/mission-control/video/meeting/${meetingId}/sendToCallback`,
};

const getVideoProviderSettings = (videoProvider: VideoProvider) =>
    VIDEO_PROVIDER_SETTINGS[videoProvider] ?? VIDEO_PROVIDER_SETTINGS.ZOOM;

// eslint-disable-next-line import/group-exports
class VideoService {
    // @ts-ignore
    private history: History = null;
    private _meetingId: string | null = sessionStorage.getItem(MEETING_ID_KEY) || null;
    private _statusUrl: string | null = sessionStorage.getItem(VIDEO_CALL_STATUS_URL_ID_KEY) || null;
    private incomingMeetingsShortPollingId: number | null = null;
    private videoCallStatusPollingId: number | null = null;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    private agent = window?.myCPP?.agent as {getState: () => AgentState};

    public baseShortPolling = 5000;
    public patientVideoCallStatusShortPolling = 10000;

    constructor() {
        return this;
    }

    get meetingId(): string | null {
        return this._meetingId;
    }

    set meetingId(value: string | null) {
        if (value !== null) {
            sessionStorage.setItem(MEETING_ID_KEY, value?.toString());
        } else {
            sessionStorage.removeItem(MEETING_ID_KEY);
        }
        this._meetingId = value;
    }

    get statusUrl(): string | null {
        return this._statusUrl;
    }

    set statusUrl(value: string | null) {
        if (value !== null) {
            sessionStorage.setItem(VIDEO_CALL_STATUS_URL_ID_KEY, value);
        } else {
            sessionStorage.removeItem(VIDEO_CALL_STATUS_URL_ID_KEY);
        }
        this._statusUrl = value;
    }

    private _getAgentState = () => {
        return this.agent?.getState();
    };

    private _getAgentName = () => {
        return this._getAgentState()?.name;
    };

    private _getIsDoNotDisturbEnabled = () => {
        const userData = this._getUserData();
        return userData?.phoneCallData?.isDoNotDisturbedEnabled;
    };

    private _getUserData = () => {
        return (store.getState() as VideoStore).commonUserDataReducer;
    };

    private _getAwsConnectStatus = () => {
        const userData = this._getUserData();
        return userData?.phoneCallData?.awsConnectAgentStatus;
    };

    private _openVideoCall({
        videoProvider,
        callData,
        patientId: argPatientId,
    }: {
        videoProvider: VideoProvider;
        callData: CallData;
        patientId?: number;
    }) {
        try {
            this.clearVideoCallStatusPollingId();

            const settings = getVideoProviderSettings(videoProvider);

            const meetingId = getMeetingIdFromCallData(callData);
            const patientId = callData?.patientId ?? argPatientId;

            awsConnectService.onAVideoCall(() => {
                awsConnectService.setStoreStatus(ON_VIDEO_CALL);
                this.handleAwsStatus({meetingId: meetingId, statusUrl: settings.providerSpecificBaseUrl});
            });

            if (settings.isWidget) {
                vidyoProviderLogs({
                    message: `Call accepted, opening video call widget. Meeting id: ${meetingId}, patient id: ${patientId}`,
                    eventType: 'device',
                    status: 'info',
                });
                this._openVideoCallWidget({
                    meetingId,
                    patientId,
                });
                return;
            }

            const callUrl = getZoomConnectUrlFromCallData(callData);

            if (!callUrl) {
                console.error('Join url for ZOOM is not provided');
                return;
            }

            this._openNativeApp({
                meetingId,
                providerUrl: settings.providerSpecificBaseUrl,
                joinUrl: callUrl,
            });
        } catch (e) {
            console.error(e);
        }
    }

    private _openNativeAppUrl(startUrl?: string): void {
        if (!startUrl) {
            console.error('Start url is not provided');
            return;
        }

        const win = window.open(startUrl, '_blank');
        win.focus();
        setTimeout(() => {
            win.close();
        }, 15000);
    }

    private _openNativeApp({
        joinUrl,
        meetingId,
        providerUrl,
    }: {
        meetingId: string;
        joinUrl: string;
        providerUrl: string;
    }): void {
        window.DISABLE_AWS_LOGIN === TRUE && this._acceptCall({meetingId, url: providerUrl});
        this._openNativeAppUrl(joinUrl);
    }

    private _openVideoCallWidget({meetingId, patientId}: {meetingId: string; patientId?: number}): void {
        store.dispatch(openVideoCallWidget({meetingId, patientId}));
    }

    private _acceptCall({meetingId, url}: {meetingId: string; url: string}) {
        httpApi
            .put({
                url: URLS.ACCEPT_CALL({
                    url,
                    meetingId,
                }),
            })
            .catch((e) => console.error(e));
    }

    private _handleIncomingMeetings = (declinedMeetingId?: string) => {
        const {isCppAgentInitialized} = awsConnectService;

        const awsStatus: string = this._getAwsConnectStatus();
        const agentName = this._getAgentName();

        const agentUnavailable =
            (agentName === OFFLINE && !this._getIsDoNotDisturbEnabled()) || agentName === ON_VIDEO_CALL;
        const awsUnavailable =
            window.DISABLE_AWS_LOGIN === TRUE || !isCppAgentInitialized() || awsStatus === ON_VIDEO_CALL;

        let activeVideoCallsList: CallData[] = (store.getState() as VideoStore).callReducer.activeVideoCallsList;

        // Remove declined meeting from active list
        if (declinedMeetingId) {
            activeVideoCallsList = activeVideoCallsList?.filter((call) => call.meetingId !== declinedMeetingId) || [];
        }

        if (agentUnavailable && awsUnavailable) {
            return Promise.resolve();
        }

        return httpApi
            .get<GetVideoCallsResponseDTO>({
                url: URLS.VIDEO(PROVIDER_AGNOSTIC_BASE_URL),
            })
            .then((data: GetVideoCallsResponseDTO) => {
                const newActiveVideoCallsList = mapVideoCallResponseToActiveCall(data);
                const isNewCall = activeVideoCallsList?.length < newActiveVideoCallsList?.length;
                const isCallHandled = activeVideoCallsList?.length > newActiveVideoCallsList?.length;

                const curCall = newActiveVideoCallsList.slice(-1)?.[0];

                if (isNewCall && curCall?.meetingId) {
                    this._notifyOnCall(curCall);
                }

                if (isCallHandled) {
                    store.dispatch(updateVideoNotifications(newActiveVideoCallsList));
                }

                store.dispatch(updateVideoCallsList(newActiveVideoCallsList));
            });
    };

    private _cleanCallData = () => {
        const activeVideoCallsList: CallData[] = (store.getState() as VideoStore).callReducer.activeVideoCallsList;
        const listWithoutCurrentCall = activeVideoCallsList.filter(({meetingId}) => meetingId !== this.meetingId);
        store.dispatch(updateVideoCallsList(listWithoutCurrentCall));
        this.meetingId = null;
        this.statusUrl = null;
        store.dispatch(closeVideoCallWidget());
        this.clearVideoCallStatusPollingId();
        this.removeVideoCallDataLocalStorage();
    };

    private _notifyOnCall(callerPatient: CallData): void {
        const {stopNodes, playNodes, createAudioNodes} = audioServiceFactory();
        createAudioNodes(
            () => {
                store.dispatch(
                    showCallNotification({
                        ...callerPatient,
                        mountCb: playNodes,
                        unmountCb: stopNodes,
                    }),
                );
            },
            {loop: true, isPERS: ''},
        );
    }

    private async _checkPatientVideoCallStatus(patientId: number, callback: (data: ActivePatientResponseDTO) => void) {
        await httpApi
            .get<ActivePatientResponseDTO>({
                url: URLS.VIDEO_ACTIVE({
                    url: PROVIDER_AGNOSTIC_BASE_URL,
                    patientId,
                }),
            })
            .then((bodyResponse: ActivePatientResponseDTO) => callback(bodyResponse));
    }

    public saveVideoCallDataLocalStorage = (data: ActiveCall) => {
        localStorage.setItem(VIDEO_CALL_DATA_KEY, JSON.stringify(data));
    };

    public loadVideoCallDataLocalStorage = (): ActiveCall => {
        const data = localStorage.getItem(VIDEO_CALL_DATA_KEY);
        return data ? (JSON.parse(data) as ActiveCall) : null;
    };

    public removeVideoCallDataLocalStorage = () => {
        localStorage.removeItem(VIDEO_CALL_DATA_KEY);
    };

    public handleAwsStatus = ({meetingId, statusUrl}: {meetingId: string; statusUrl: string}) => {
        this.meetingId = meetingId;
        this.statusUrl = statusUrl;

        clearInterval(this.videoCallStatusPollingId);
        this.videoCallStatusPollingId = setInterval(() => {
            httpApi
                .get<VideoCallStatus>({
                    url: URLS.STATUS({
                        url: statusUrl,
                        meetingId,
                    }),
                })
                .then((res: VideoCallStatus) => {
                    if (
                        res?.videoMeetingStatusDTO.activeUserStatus === 'LEFT' ||
                        res?.videoMeetingStatusDTO.meetingStatus === 'ENDED' ||
                        res?.videoMeetingStatusDTO.meetingStatus === 'CANCELED' ||
                        res?.videoMeetingStatusDTO.meetingStatus === 'SUSPENDED'
                    ) {
                        const agentName = this._getAgentName();
                        const awsConnectAgentStatus = this._getAwsConnectStatus();

                        const isOnVideoCall = agentName === ON_VIDEO_CALL || awsConnectAgentStatus === ON_VIDEO_CALL;

                        if (isOnVideoCall) {
                            if (this._getIsDoNotDisturbEnabled()) {
                                awsConnectService.goOffline();
                            } else {
                                awsConnectService.goAvailable();
                            }
                        }

                        this._cleanCallData();
                    }
                })
                .catch((e) => {
                    console.error(e);
                    this.clearVideoCallStatusPollingId();
                });
        }, this.baseShortPolling) as unknown as number;
    };

    public leaveCall({meetingId, url}: {meetingId: string; url: string}) {
        vidyoProviderLogs({
            message: `Leaving call. Meeting id: ${meetingId}`,
            eventType: 'connection',
            status: 'info',
        });

        return httpApi
            .post({
                url: URLS.LEAVE_CALL({
                    url,
                    meetingId,
                }),
            })
            .catch((e) => console.error(e))
            .finally(() => {
                this._cleanCallData();
                if (this._getIsDoNotDisturbEnabled()) {
                    awsConnectService.goOffline();
                } else {
                    awsConnectService.goAvailable();
                }
            });
    }

    public async declineCall({meetingId, url}: {meetingId: string; url: string}) {
        vidyoProviderLogs({
            message: `Declining call. Meeting id: ${meetingId}`,
            eventType: 'connection',
            status: 'info',
        });
        await httpApi.put({
            url: URLS.DECLINE_CALL({
                url,
                meetingId,
            }),
        });
        this.removeVideoCallDataLocalStorage();
        store.dispatch(closeVideoCallWidget());
        await this._handleIncomingMeetings(meetingId);
    }

    public createVideoMeeting = (patientId: number) => {
        if (!patientId) {
            vidyoProviderLogs({
                message: 'Patient id is not provided',
                eventType: 'connection',
                status: 'error',
            });
            return;
        }

        return httpApi
            .post<VideoCallResponseDTO>({
                url: URLS.PATIENT({
                    url: PROVIDER_AGNOSTIC_BASE_URL,
                    patientId,
                }),
            })
            .then((res: VideoCallResponseDTO) => {
                const callData = res?.videoMeeting;
                const videoProvider = res?.videoProvider;

                if (!callData || !videoProvider) {
                    vidyoProviderLogs({
                        message: 'Call data or video provider is not provided',
                        eventType: 'connection',
                        status: 'error',
                    });
                    return;
                }

                this.saveVideoCallDataLocalStorage(callData);

                this._openVideoCall({
                    videoProvider: videoProvider,
                    callData,
                    patientId,
                });
            });
    };

    public async initiatePatientStatusShortPolling(
        patientId: number,
        callback: (data: ActivePatientResponseDTO) => void,
    ): Promise<number> {
        try {
            await this._checkPatientVideoCallStatus(patientId, callback);
            return setInterval(() => {
                this._checkPatientVideoCallStatus(patientId, callback).catch((e) => e && console.error(e));
            }, this.patientVideoCallStatusShortPolling) as unknown as number;
        } catch (e) {
            console.error(e);
            return 0;
        }
    }

    public initiateIncomingMeetingsShortPolling() {
        this.incomingMeetingsShortPollingId = setInterval(() => {
            this._handleIncomingMeetings()?.catch((e) => e && console.error(e));
        }, this.baseShortPolling) as unknown as number;

        loggerService.logEvent<LogEventDataType>({
            severity: 'Info',
            email: this._getUserData()?.user?.email,
            action: LoggingActions.initiateShortPolling,
            message: `Zoom short polling initialized with id ${
                this.incomingMeetingsShortPollingId
            }. AWS connect status was: in store - ${this._getAwsConnectStatus()}; in awsClient - ${this._getAgentName()}`,
        });
    }

    public clearIncomingMeetingsShortPolling = () => {
        clearInterval(this.incomingMeetingsShortPollingId);

        loggerService.logEvent<LogEventDataType>({
            severity: 'Info',
            email: this._getUserData()?.user?.email,
            action: LoggingActions.stopShortPolling,
            message: `Video short polling with id ${
                this.incomingMeetingsShortPollingId
            } was stopped. AWS connect status was: in store - ${this._getAwsConnectStatus()}; in awsClient - ${this._getAgentName()}`,
        });

        this.incomingMeetingsShortPollingId = null;
    };

    public clearVideoCallStatusPollingId = () => {
        clearInterval(this.videoCallStatusPollingId);
        this.videoCallStatusPollingId = null;
    };

    public handleAcceptCall = (caller: CallData) => {
        const acceptCall = () => {
            const meetingId = getMeetingIdFromCallData(caller);
            const videoProvider = caller?.videoProvider;
            const videoProviderSettings = getVideoProviderSettings(videoProvider);
            const providerUrl = videoProviderSettings.providerSpecificBaseUrl;

            if (!meetingId || !providerUrl) {
                console.error('Meeting id or provider url is not provided');
                console.error({meetingId, providerUrl});
                return;
            }

            this.saveVideoCallDataLocalStorage(caller);

            this._openVideoCall({
                callData: caller,
                videoProvider,
            });

            this._acceptCall({meetingId, url: providerUrl});
            store.dispatch(updateVideoNotifications([]));
        };

        if (awsConnectService.checkIfConnectionExists()) {
            awsConnectService.disconnectContact(acceptCall);
            return;
        }

        acceptCall();
    };

    public removePatientStatusShortPolling = (id: number) => {
        clearInterval(id);
    };

    public setHistory = (history: History) => (this.history = history);

    public getSharedLink = ({meetingId, portalUrl}: {meetingId: string; portalUrl: string}) => {
        return URLS.SHARED_LINK_URL({meetingId, portalUrl});
    };
    public getNewWindowLink = ({meetingId, portalUrl}: {meetingId: string; portalUrl: string}) => {
        return URLS.OPEN_NEW_WINDOW_LINK_URL({meetingId, portalUrl});
    };
    public inviteTranslator = async (args: {meetingId: string; sipNumber: string}): Promise<void> => {
        vidyoProviderLogs({
            message: `Inviting translator. Meeting id: ${args.meetingId}, SIP number: ${args.sipNumber}`,
            eventType: 'device',
            status: 'info',
        });
        await httpApi.post({
            url: URLS.INVITE_TRANSLATOR({
                meetingId: args.meetingId,
                sipNumber: args.sipNumber,
                url: VIDEO_PROVIDER_SETTINGS.VIDYO.providerSpecificBaseUrl,
            }),
        });
    };
    public sendToCallBell = (meetingId: string): Promise<void> =>
        httpApi.put({
            url: URLS.VIDEO_SEND_TO_CALLBACK(meetingId),
        });
}

const videoProvider = new VideoService();

export {
    getVideoProviderSettings,
    mapVideoCallResponseToActiveCall,
    MEETING_ID_KEY,
    PROVIDER_AGNOSTIC_BASE_URL,
    VIDEO_CALL_STATUS_URL_ID_KEY,
    VIDEO_CALL_USER_KEY,
    VIDEO_PROVIDER_SETTINGS,
    videoProvider,
    VideoService,
};
